/**
 * Índice de hojas al estilo Looker: iconos, acentos y árbol sección → página.
 */
import React from 'react';

export const PAGE_NAV_ICONS = [
    { id: 'page', label: 'Página' },
    { id: 'chart', label: 'Gráfico' },
    { id: 'globe', label: 'Sitio web' },
    { id: 'users', label: 'Audiencia' },
    { id: 'mail', label: 'Correo' },
    { id: 'target', label: 'Objetivos' },
    { id: 'sparkle', label: 'Destacado' },
    { id: 'folder', label: 'Carpeta' },
    { id: 'table', label: 'Tabla' },
    { id: 'megaphone', label: 'Medios' },
    { id: 'calendar', label: 'Calendario' },
    { id: 'image', label: 'Imagen' },
];

export const PAGE_NAV_ACCENTS = [
    { id: '', label: 'Predeterminado', color: '#1c1d21' },
    { id: 'red', label: 'Rojo', color: '#c5221f' },
    { id: 'blue', label: 'Azul', color: '#1a73e8' },
    { id: 'teal', label: 'Verde', color: '#00897b' },
    { id: 'purple', label: 'Morado', color: '#5E35B1' },
    { id: 'orange', label: 'Naranja', color: '#e37400' },
    { id: 'pink', label: 'Rosa', color: '#c2185b' },
];

const ACCENT_MAP = Object.fromEntries(PAGE_NAV_ACCENTS.map((item) => [item.id, item.color]));

export function accentHex(accent, fallback = '#1c1d21') {
    if (!accent) return fallback;
    return ACCENT_MAP[accent] || fallback;
}

export function NavGlyph({ icon = 'page', size = 18, color = 'currentColor' }) {
    const props = {
        width: size,
        height: size,
        viewBox: '0 0 24 24',
        fill: 'none',
        stroke: color,
        strokeWidth: 1.8,
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        'aria-hidden': true,
    };
    switch (icon) {
        case 'chart':
            return (
                <svg {...props}>
                    <polyline points="3 17 9 11 13 15 21 7" />
                    <polyline points="15 7 21 7 21 13" />
                </svg>
            );
        case 'globe':
            return (
                <svg {...props}>
                    <circle cx="12" cy="12" r="9" />
                    <path d="M3 12h18" />
                    <path d="M12 3a15 15 0 0 1 0 18" />
                    <path d="M12 3a15 15 0 0 0 0 18" />
                </svg>
            );
        case 'users':
            return (
                <svg {...props}>
                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="3" />
                    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                    <path d="M16 3.13a3 3 0 0 1 0 5.75" />
                </svg>
            );
        case 'mail':
            return (
                <svg {...props}>
                    <rect x="3" y="5" width="18" height="14" rx="2" />
                    <path d="M3 7l9 6 9-6" />
                </svg>
            );
        case 'target':
            return (
                <svg {...props}>
                    <circle cx="12" cy="12" r="9" />
                    <circle cx="12" cy="12" r="5" />
                    <circle cx="12" cy="12" r="1.5" />
                </svg>
            );
        case 'sparkle':
            return (
                <svg {...props}>
                    <path d="M12 3l1.4 4.2L18 8.6l-4.2 1.4L12 14l-1.4-4-4.2-1.4 4.2-1.4z" />
                    <path d="M19 14l.7 2 2 .7-2 .7-.7 2-.7-2-2-.7 2-.7z" />
                </svg>
            );
        case 'folder':
            return (
                <svg {...props}>
                    <path d="M3 7a2 2 0 0 1 2-2h5l2 2h7a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                </svg>
            );
        case 'table':
            return (
                <svg {...props}>
                    <rect x="3" y="4" width="18" height="16" rx="2" />
                    <path d="M3 10h18M9 10v10M15 10v10" />
                </svg>
            );
        case 'megaphone':
            return (
                <svg {...props}>
                    <path d="M4 10v4h3l6 4V6L7 10z" />
                    <path d="M19 8a5 5 0 0 1 0 8" />
                </svg>
            );
        case 'calendar':
            return (
                <svg {...props}>
                    <rect x="3" y="5" width="18" height="16" rx="2" />
                    <path d="M8 3v4M16 3v4M3 11h18" />
                </svg>
            );
        case 'image':
            return (
                <svg {...props}>
                    <rect x="3" y="5" width="18" height="14" rx="2" />
                    <circle cx="9" cy="10" r="1.6" />
                    <path d="M21 16l-5-5-9 9" />
                </svg>
            );
        default:
            return (
                <svg {...props}>
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                </svg>
            );
    }
}

export function createReportSection(name = 'Sección', overrides = {}) {
    return {
        id: `sec-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        name,
        icon: 'folder',
        accent: '',
        hidden: false,
        ...overrides,
    };
}

export function normalizeReportSections(raw = []) {
    if (!Array.isArray(raw)) return [];
    return raw.map((section, index) => ({
        id: section.id || `sec-${index + 1}`,
        name: String(section.name || `Sección ${index + 1}`).trim() || `Sección ${index + 1}`,
        icon: PAGE_NAV_ICONS.some((item) => item.id === section.icon) ? section.icon : 'folder',
        accent: PAGE_NAV_ACCENTS.some((item) => item.id === section.accent) ? section.accent : '',
        hidden: Boolean(section.hidden),
    }));
}

export function withPageNavDefaults(page = {}) {
    return {
        ...page,
        icon: PAGE_NAV_ICONS.some((item) => item.id === page.icon) ? page.icon : 'page',
        accent: PAGE_NAV_ACCENTS.some((item) => item.id === page.accent) ? page.accent : '',
        hidden: Boolean(page.hidden),
        sectionId: page.sectionId || null,
    };
}

export function buildPageOutline(pages = [], sections = []) {
    const sectionMap = Object.fromEntries(sections.map((section) => [section.id, section]));
    const grouped = {};
    sections.forEach((section) => {
        grouped[section.id] = [];
    });
    const nodes = [];
    const emitted = new Set();
    pages.forEach((page) => {
        const section = page.sectionId ? sectionMap[page.sectionId] : null;
        if (section) {
            grouped[section.id].push(page);
            if (!emitted.has(section.id)) {
                emitted.add(section.id);
                nodes.push({ type: 'section', section, pages: grouped[section.id] });
            }
        } else {
            nodes.push({ type: 'page', page: withPageNavDefaults(page) });
        }
    });
    sections.forEach((section) => {
        if (!emitted.has(section.id)) {
            nodes.push({ type: 'section', section, pages: grouped[section.id] || [] });
        }
    });
    return nodes;
}

export function viewerPages(pages = [], sections = []) {
    const hiddenSection = new Set(sections.filter((section) => section.hidden).map((section) => section.id));
    return pages.filter((page) => !page.hidden && !hiddenSection.has(page.sectionId));
}

function cloneOutline(pages, sections) {
    return buildPageOutline(pages, sections).map((node) => (
        node.type === 'section'
            ? { type: 'section', section: node.section, pages: [...node.pages] }
            : { type: 'page', page: node.page }
    ));
}

function extractOutlineItem(nodes, from) {
    for (let i = 0; i < nodes.length; i += 1) {
        const node = nodes[i];
        if (from.type === 'section' && node.type === 'section' && node.section.id === from.id) {
            return nodes.splice(i, 1)[0];
        }
        if (from.type === 'page' && node.type === 'page' && node.page.id === from.id) {
            return nodes.splice(i, 1)[0];
        }
        if (from.type === 'page' && node.type === 'section') {
            const index = node.pages.findIndex((page) => page.id === from.id);
            if (index >= 0) {
                const [page] = node.pages.splice(index, 1);
                return { type: 'page', page };
            }
        }
    }
    return null;
}

function insertOutlineItem(nodes, item, to) {
    if (!item || !to) return false;
    if (item.type === 'section') {
        let index = nodes.findIndex((node) => (
            (to.type === 'section' && node.type === 'section' && node.section.id === to.id)
            || (to.type === 'page' && node.type === 'page' && node.page.id === to.id)
        ));
        if (index < 0 && to.type === 'page') {
            index = nodes.findIndex((node) => node.type === 'section' && node.pages.some((page) => page.id === to.id));
        }
        if (index < 0) return false;
        const at = to.place === 'after' ? index + 1 : index;
        nodes.splice(at, 0, item);
        return true;
    }

    if (to.type === 'section' && (to.place === 'inside' || to.place === 'child')) {
        const host = nodes.find((node) => node.type === 'section' && node.section.id === to.id);
        if (!host) return false;
        if (to.place === 'child' && to.childId) {
            const childIndex = host.pages.findIndex((page) => page.id === to.childId);
            const at = childIndex < 0 ? host.pages.length : (to.childPlace === 'after' ? childIndex + 1 : childIndex);
            host.pages.splice(at, 0, item.page);
        } else {
            host.pages.push(item.page);
        }
        return true;
    }

    if (to.type === 'section') {
        const index = nodes.findIndex((node) => node.type === 'section' && node.section.id === to.id);
        if (index < 0) return false;
        nodes.splice(to.place === 'after' ? index + 1 : index, 0, item);
        return true;
    }

    const topIndex = nodes.findIndex((node) => node.type === 'page' && node.page.id === to.id);
    if (topIndex >= 0) {
        nodes.splice(to.place === 'after' ? topIndex + 1 : topIndex, 0, item);
        return true;
    }

    const host = nodes.find((node) => node.type === 'section' && node.pages.some((page) => page.id === to.id));
    if (!host) return false;
    const childIndex = host.pages.findIndex((page) => page.id === to.id);
    host.pages.splice(to.place === 'after' ? childIndex + 1 : childIndex, 0, item.page);
    return true;
}

export function applyOutlineDrop(pages, sections, from, to) {
    if (!from?.id || !to?.id || !from.type || !to.type) return null;
    const target = (from.type === 'page' && to.type === 'section')
        ? { ...to, place: 'inside' }
        : to;
    if (from.type === target.type && from.id === target.id && target.place !== 'inside') return null;
    if (from.type === 'section' && target.place === 'inside') return null;
    if (from.type === 'section' && target.type === 'page' && target.place === 'inside') return null;

    const nodes = cloneOutline(pages, sections);
    const item = extractOutlineItem(nodes, from);
    if (!item) return null;
    if (!insertOutlineItem(nodes, item, target)) return null;

    const nextPages = [];
    const order = [];
    nodes.forEach((node) => {
        if (node.type === 'page') {
            nextPages.push({ ...node.page, sectionId: null });
            return;
        }
        order.push(node.section.id);
        node.pages.forEach((page) => {
            nextPages.push({ ...page, sectionId: node.section.id });
        });
    });
    const byId = Object.fromEntries(sections.map((section) => [section.id, section]));
    const nextSections = order.map((id) => byId[id]).filter(Boolean);
    sections.forEach((section) => {
        if (!order.includes(section.id)) nextSections.push(section);
    });
    if (!nextPages.length) return null;
    return { pages: nextPages, sections: nextSections };
}

export function pageNavPayload(page) {
    const next = withPageNavDefaults(page);
    return {
        icon: next.icon,
        accent: next.accent,
        hidden: next.hidden,
        sectionId: next.sectionId || null,
    };
}
